import { cn } from "@/lib/utils";

// Base skeleton with shimmer animation
function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div 
      className={cn(
        "animate-pulse rounded-md bg-muted/60 relative overflow-hidden",
        "after:absolute after:inset-0 after:-translate-x-full after:animate-[shimmer_2s_infinite]",
        "after:bg-gradient-to-r after:from-transparent after:via-muted/30 after:to-transparent",
        className
      )} 
      {...props} 
    />
  );
}

// Chat message skeleton with typing animation
function ChatMessageSkeleton({ isUser = false }: { isUser?: boolean }) {
  return (
    <div className={cn("flex gap-3 max-w-[85%]", isUser ? "ml-auto flex-row-reverse" : "mr-auto")}>
      {!isUser && (
        <Skeleton className="flex-shrink-0 w-8 h-8 rounded-full" />
      )}
      <div className="flex flex-col gap-1 flex-1">
        <Skeleton className={cn(
          "rounded-2xl px-4 py-3 min-h-[60px]",
          isUser ? "rounded-br-sm" : "rounded-bl-sm"
        )} />
      </div>
    </div>
  );
}

// Closet item skeleton
function ClosetItemSkeleton() {
  return (
    <div className="aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden">
      <Skeleton className="w-full h-full" />
    </div>
  );
}

// Closet grid skeleton
function ClosetGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4 mt-4">
      {Array.from({ length: count }).map((_, i) => (
        <ClosetItemSkeleton key={i} />
      ))}
    </div>
  );
}

// Feed post skeleton
function FeedPostSkeleton() {
  return (
    <div className="break-inside-avoid border p-4 rounded-2xl bg-card border-border">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Skeleton className="w-8 h-8 rounded-full" />
          <Skeleton className="w-24 h-3 rounded" />
        </div>
        <Skeleton className="w-16 h-3 rounded" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Skeleton className="w-full aspect-[3/4] rounded-lg" />
        <div className="grid grid-rows-2 gap-2">
          <Skeleton className="w-full h-full rounded-lg" />
          <Skeleton className="w-full h-full rounded-lg" />
        </div>
      </div>
      <Skeleton className="h-5 w-3/4 mb-2 rounded" />
      <Skeleton className="h-3 w-full mb-3 rounded" />
      <div className="flex justify-between pt-3 border-t border-border">
        <Skeleton className="w-12 h-4 rounded" />
        <Skeleton className="w-16 h-4 rounded" />
      </div>
    </div>
  );
}

// Feed skeleton
function FeedSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <FeedPostSkeleton key={i} />
      ))}
    </div>
  );
}

// Profile skeleton
function ProfileSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in">
      <div className="p-6 rounded-3xl border bg-card border-border">
        <div className="flex items-center gap-5">
          <Skeleton className="w-20 h-20 rounded-2xl" />
          <div className="flex-1 space-y-3">
            <Skeleton className="w-16 h-2 rounded" />
            <Skeleton className="w-32 h-6 rounded" />
            <Skeleton className="w-24 h-6 rounded-full" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-2xl" />
        ))}
      </div>
      <Skeleton className="h-32 rounded-2xl" />
    </div>
  );
}

// Weather widget skeleton
function WeatherSkeleton() {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-card/50 border border-border/50">
      <Skeleton className="w-4 h-4 rounded-full" />
      <Skeleton className="w-12 h-3 rounded" />
    </div>
  );
}

// Outfit card skeleton
function OutfitCardSkeleton() {
  return (
    <div className="p-4 rounded-2xl border bg-card border-border space-y-4">
      <div className="flex gap-2">
        <Skeleton className="w-24 h-32 rounded-lg" />
        <Skeleton className="w-24 h-32 rounded-lg" />
        <Skeleton className="w-24 h-32 rounded-lg" />
      </div>
      <Skeleton className="h-4 w-3/4 rounded" />
      <Skeleton className="h-3 w-full rounded" />
      <div className="flex gap-2">
        <Skeleton className="h-8 w-20 rounded-full" />
        <Skeleton className="h-8 w-20 rounded-full" />
      </div>
    </div>
  );
}

// Challenge skeleton
function ChallengeSkeleton() {
  return (
    <div className="p-6 rounded-2xl border bg-card border-border space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="w-32 h-6 rounded" />
        <Skeleton className="w-20 h-6 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full rounded" />
      <Skeleton className="h-4 w-2/3 rounded" />
      <Skeleton className="h-10 w-full rounded-xl" />
    </div>
  );
}

// Stats card skeleton
function StatsCardSkeleton() {
  return (
    <div className="p-4 rounded-2xl border bg-card border-border text-center">
      <Skeleton className="w-6 h-6 mx-auto mb-2 rounded" />
      <Skeleton className="w-12 h-8 mx-auto mb-1 rounded" />
      <Skeleton className="w-16 h-2 mx-auto rounded" />
    </div>
  );
}

// Notification skeleton
function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-3 p-4 border-b border-border">
      <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4 rounded" />
        <Skeleton className="h-3 w-full rounded" />
        <Skeleton className="h-2 w-16 rounded" />
      </div>
    </div>
  );
}

// Modal content skeleton
function ModalContentSkeleton() {
  return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-8 w-1/2 rounded" />
      <Skeleton className="h-4 w-full rounded" />
      <Skeleton className="h-4 w-3/4 rounded" />
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-10 w-full rounded-xl" />
    </div>
  );
}

// Typing indicator for streaming chat
function TypingIndicator() {
  return (
    <div className="flex gap-1 items-center px-4 py-3">
      <div className="w-2 h-2 rounded-full bg-aura-gold animate-bounce" style={{ animationDelay: "0ms" }} />
      <div className="w-2 h-2 rounded-full bg-aura-gold animate-bounce" style={{ animationDelay: "150ms" }} />
      <div className="w-2 h-2 rounded-full bg-aura-gold animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

// Page loader skeleton
function PageLoaderSkeleton() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-aura-gold/20 to-aura-rose/20 animate-pulse" />
        <Skeleton className="w-32 h-2 rounded" />
      </div>
    </div>
  );
}

export { 
  Skeleton,
  ChatMessageSkeleton,
  ClosetItemSkeleton,
  ClosetGridSkeleton,
  FeedPostSkeleton,
  FeedSkeleton,
  ProfileSkeleton,
  WeatherSkeleton,
  OutfitCardSkeleton,
  ChallengeSkeleton,
  StatsCardSkeleton,
  NotificationSkeleton,
  ModalContentSkeleton,
  TypingIndicator,
  PageLoaderSkeleton,
};
